import React, { ChangeEvent, FC, useEffect, useState } from 'react';

import { Select, Text } from '@components/common';
import { rangeMap } from '@components/utils';

import * as BlockableComponents from '../../../types/blockable-components';

const DEFAULT_MIN = 1;
const DEFAULT_MAX = 5;

export const LinearScale: FC<BlockableComponents.LinearScale> = ({
  value: initialValue,
  options: initialOptions,
  isEditable,
  onChange,
  onChangeOptions,
  className
}) => {
  const [value, setValue] = useState<BlockableComponents.LinearScale['value']>();
  const [options, setOptions] = useState<BlockableComponents.LinearScale['options']>([]);
  const [min, setMin] = useState(DEFAULT_MIN);
  const [max, setMax] = useState(DEFAULT_MAX);

  const handleOnLabelChange = (e: ChangeEvent<HTMLInputElement>, position: number) => {
    if (options) {
      const newOptions = [...options];
      const index = newOptions.findIndex((option) => option.position === position);

      if (index >= 0) {
        newOptions[index] = { ...newOptions[index], label: e.target.value };
        setOptions(newOptions);
        onChangeOptions?.(newOptions);
      }
    }
  };

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = +e.target.value;

    if (newValue >= min && newValue <= max) {
      setValue(newValue);
      onChange?.(newValue);
    }
  };

  const handleMinMaxChange = (min: number, max: number) => {
    const newOptions: BlockableComponents.LinearScale['options'] = [];

    for (let i = min; i <= max; i++) {
      const existingOption = options?.find((option) => option.position === i);
      newOptions.push({ position: existingOption?.position ?? i, label: existingOption?.label ?? '' });
    }

    setMin(min);
    setMax(max);
    setOptions(newOptions);
    onChangeOptions?.(newOptions);
  };

  useEffect(() => {
    if (initialOptions) {
      setOptions(initialOptions);
      setMin(initialOptions[0].position);
      setMax(initialOptions[initialOptions.length - 1].position);
    }
  }, [initialOptions]);

  useEffect(() => {
    if (initialValue) {
      setValue(initialValue);
    }
  }, [initialValue]);

  return (
    <section>
      {isEditable && (
        <div className='mb-3 mt-2 flex items-center space-x-2'>
          <Text className='text-sm'>From</Text>
          <Select
            maxH={48}
            options={rangeMap(1, max - 1, (i) => ({ label: String(i), value: String(i) }))}
            value={String(min)}
            onChange={(value) => handleMinMaxChange(+value, max)}
          />
          <Text className='text-sm'>to</Text>
          <Select
            maxH={48}
            options={rangeMap(min + 1, 10, (i) => ({ label: String(i), value: String(i) }))}
            value={String(max)}
            onChange={(value) => handleMinMaxChange(min, +value)}
          />
        </div>
      )}
      <div className={className}>
        {options &&
          options.map(({ position, label }) => (
            <div key={`option-${position}`} className='mb-3 flex items-center space-x-5'>
              <div className='flex items-center space-x-2'>
                {!isEditable && (
                  <input
                    aria-label={`Linear scale option ${position}`}
                    id={`option-${position}`}
                    type='radio'
                    name='linear-scale'
                    value={position}
                    onChange={handleOnChange}
                    checked={value === position}
                  />
                )}
                <Text className='w-2 text-sm'>{position}</Text>
              </div>
              {isEditable ? (
                <input
                  type='text'
                  placeholder='Add label'
                  className='w-full max-w-sm border-0 p-0 px-1 text-sm placeholder-gray-400'
                  value={label}
                  onChange={(e) => handleOnLabelChange(e, +position)}
                />
              ) : (
                <Text as='label' htmlFor={`option-${position}`} className='cursor-pointer text-sm'>
                  {label}
                </Text>
              )}
            </div>
          ))}
      </div>
    </section>
  );
};
